'use client';

import React from 'react';
import { Slot } from '@radix-ui/react-slot';

import {
  cx,
  Color,
  HorizontalPosition,
  HorizontalPositions,
  VerticalPosition,
  VerticalPositions,
  getColorClassNames,
  colorPalette,
} from '@UI/lib/utils';

const parseDecorationAlignment = (decorationAlignment: string) => {
  if (!decorationAlignment) return '';
  switch (decorationAlignment) {
    case HorizontalPositions.Left:
      return 'border-l-4';
    case VerticalPositions.Top:
      return 'border-t-4';
    case HorizontalPositions.Right:
      return 'border-r-4';
    case VerticalPositions.Bottom:
      return 'border-b-4';
    default:
      return '';
  }
};

interface CardProps extends React.ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
  decoration?: HorizontalPosition | VerticalPosition | '';
  decorationColor?: Color;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (props, forwardedRef) => {
    const {
      asChild,
      decoration = '',
      decorationColor,
      children,
      className,
      ...other
    } = props;
    const Component = asChild ? Slot : 'div';
    return (
      <Component
        ref={forwardedRef}
        className={cx(
          // common
          'relative w-full text-left ring-1 rounded-revving-default p-6',
          // light
          'bg-revving-background ring-revving-ring shadow-revving-card',
          // dark
          'dark:bg-darkRevving-background dark:ring-darkRevving-ring dark:shadow-darkRevving-card',
          // brand
          decorationColor
            ? getColorClassNames(decorationColor, colorPalette.border)
                .borderColor
            : 'border-revving-brand dark:border-darkRevving-brand',
          parseDecorationAlignment(decoration),
          className,
        )}
        {...other}
      >
        {children}
      </Component>
    );
  },
);

Card.displayName = 'Card';
export { Card, type CardProps };
